import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";

// A serializable snapshot of "where the user is" — spans the library views and
// the graph view (structural/feature, focused feature, selected node). Kept as a
// single canonical location so bookmarks and the "current location" UI can read
// it uniformly.
export interface NavLocation {
  kind: "library" | "graph";
  // library context
  reposMode?: boolean;
  rolesMode?: boolean;
  repoId?: number;
  repoPath?: string;
  roleId?: number;
  // graph context
  networkMode?: "structural" | "feature";
  networkRepoId?: number;
  featureId?: string | null;
  featureLabel?: string | null;
  selectedNode?: string | null;
}

export interface NavigationState {
  current: NavLocation;
}

// Persisted to localStorage so a browser refresh restores where the user was.
export const NAV_STORAGE_KEY = "vainglory.navLocation";

function loadSavedLocation(): NavLocation {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(NAV_STORAGE_KEY) : null;
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && (parsed.kind === "graph" || parsed.kind === "library")) return parsed as NavLocation;
    }
  } catch { /* ignore corrupt/unavailable storage */ }
  return { kind: "library" };
}

const initialState: NavigationState = { current: loadSavedLocation() };

const navigationSlice = createSlice({
  name: "navigation",
  initialState,
  reducers: {
    // Merge a partial update into the current location.
    setNavLocation: (state, action: PayloadAction<Partial<NavLocation>>) => {
      state.current = { ...state.current, ...action.payload };
    },
    resetNavLocation: (state) => {
      state.current = { kind: "library" };
    },
  },
});

export const { setNavLocation, resetNavLocation } = navigationSlice.actions;
export const selectNavLocation = (state: RootState) => state.navigation.current;

// One-line, human-readable description of a location for the dropdown entry.
export function describeLocation(loc: NavLocation): string {
  if (loc.kind === "graph") {
    const mode = loc.networkMode === "feature" ? "Feature" : "Structural";
    let s = `Graph · ${mode} · repo ${loc.networkRepoId ?? "?"}`;
    if (loc.networkMode === "feature" && loc.featureLabel) s += ` · ${loc.featureLabel}`;
    return s;
  }
  if (loc.reposMode && loc.repoId && loc.repoId > 0) return `Repo ${loc.repoId}${loc.repoPath ? ` · ${loc.repoPath}` : ""}`;
  if (loc.rolesMode) return loc.roleId ? `Roles · role ${loc.roleId}` : "Roles";
  return "Library";
}

export default navigationSlice.reducer;
